import {LoadingButton} from "@mui/lab";
import {Button, Dialog, DialogActions, Zoom} from "@mui/material";
import {useMutation} from "@tanstack/react-query";
import {useCustomNavigation} from "hooks/useCustomNavigation";
import {useQueryUpdater} from "hooks/useQueryUpdater";
import {ReactNode, useCallback, useMemo} from "react";
import {has} from "lodash";
import {useNotify, useResourceContext, useTranslate} from "react-admin";

type ActionPopupProps<TResult> = {
  show?: boolean;
  onClose?: () => void;
  queryKey?: string;
  submitQuery: () => Promise<TResult>;
  updateCacheFn?: (data: TResult) => void;
  onSuccess?: (data: TResult) => void;
  onSuccessNavigate?: string;
  submitButtonText?: string;
  cancelButtonText?: string;
  successMessage?: string;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  zIndex?: number;
  renderButtons?: (loading: boolean) => ReactNode;
  children?: React.ReactNode;
};

export const ActionPopup = <TResult, >(props: ActionPopupProps<TResult>) => {
  const t = useTranslate();

  const {navigate, state} = useCustomNavigation({
    delay: 300,
  });

  const notify = useNotify();

  const resource = useResourceContext();

  const queryKey = props.queryKey ?? resource;

  if (!queryKey) {
    throw new Error("Query key or resource key is required");
  }

  const {updateEntity} = useQueryUpdater({
    queryKey,
  });

  const mutation = useMutation({
    mutationFn: () => {
      return props.submitQuery();
    },
    onSuccess: (data) => {
      notify(props.successMessage ?? t("ra.notification.updated"), {
        type: "success",
      });

      if (props.updateCacheFn) {
        props.updateCacheFn(data);
      } else {
        updateEntity((entity) => {
          if (!has(entity, "id") || !has(data, "id")) {
            return entity;
          }

          if (entity.id !== data.id) {
            return entity;
          }

          return data;
        });
      }

      props.onSuccess?.(data);

      if (props.onSuccessNavigate) {
        navigate(props.onSuccessNavigate);
        return;
      }

      navigate("..");
    },
    onError: (error) => {
      console.error(error);
      notify(error?.message, {type: "error"});
    },
  });

  const show = useMemo(() => {
    if (props.show !== undefined) {
      return props.show;
    }

    return state === "show";
  }, [props.show, state]);

  const onClose = useCallback(() => {
    if (props.onClose) {
      props.onClose();
      return;
    }

    navigate("..");
  }, [props, navigate]);

  return (
    <Dialog
      open={show}
      onClose={onClose}
      aria-describedby="alert-dialog-slide-description"
      sx={{zIndex: props.zIndex ?? 300}}
      TransitionComponent={Zoom}
      PaperProps={{elevation: 0}}
      maxWidth={props.maxWidth}
    >
      {props.children}
      <DialogActions>
        {props.renderButtons ? (
          props.renderButtons(mutation.isPending)
        ) : (
          <>
            <Button
              onClick={() => navigate("..")}
              disabled={mutation.isPending}
            >
              {props.cancelButtonText ?? t("ra.actions.cancel")}
            </Button>
            <LoadingButton
              onClick={() => {
                mutation.mutate();
              }}
              variant="contained"
              color="error"
              loading={mutation.isPending}
            >
              {props.submitButtonText ?? t("ra.actions.save")}
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
